/**
 * Centralised auth-state storage for the Xpert web frontend.
 *
 * Why this exists (P0-3 fix):
 * --------------------------
 * Before this module, logout() only removed `xpert_token`, leaving
 * `xpert_admin_token`, `xpert_country`, `xpert_language`, and any
 * cached axios defaults behind. When User B logged in on the same
 * browser they inherited User A's prefs and (worse) any lingering
 * admin token.
 *
 * Now every user-scoped value goes through this module. `clearAllUserState`
 * resets the lot in one call, and there is exactly one list of keys
 * (`USER_SCOPED_KEYS`) to maintain.
 *
 * Conventions
 * -----------
 *   `xpert_token`         — the user's access JWT (4h TTL)
 *   `xpert_refresh_token` — the user's refresh JWT (30d TTL)
 *   `xpert_admin_token`   — admin-elevated JWT, only for /admin pages
 *   `xpert_country`       — last-selected country code
 *   `xpert_language`      — last i18n language
 *
 * NOTE: This module deliberately avoids React imports so it can be
 * unit-tested in pure Node and called from non-React code.
 */

// Every localStorage key whose value is tied to a specific user session.
// Adding a new user-scoped key? Add it here so logout() clears it.
export const USER_SCOPED_KEYS = Object.freeze([
  "xpert_token",
  "xpert_refresh_token",
  "xpert_admin_token",
  "xpert_country",
  "xpert_language",
]);

// Subscribers notified when auth state is cleared so React contexts
// (LocationContext, etc.) can reset their in-memory state.
const _clearListeners = new Set();

export function onAuthCleared(listener) {
  _clearListeners.add(listener);
  return () => _clearListeners.delete(listener);
}

function _emitCleared() {
  for (const fn of _clearListeners) {
    try { fn(); } catch { /* listener must not break logout */ }
  }
}

// ─── Token getters / setters ─────────────────────────────────────────────────

export function getUserToken() {
  return typeof window === "undefined" ? null : localStorage.getItem("xpert_token");
}

export function setUserToken(token) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem("xpert_token", token);
  else localStorage.removeItem("xpert_token");
}

export function getRefreshToken() {
  return typeof window === "undefined" ? null : localStorage.getItem("xpert_refresh_token");
}

export function setRefreshToken(token) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem("xpert_refresh_token", token);
  else localStorage.removeItem("xpert_refresh_token");
}

export function getAdminToken() {
  return typeof window === "undefined" ? null : localStorage.getItem("xpert_admin_token");
}

export function setAdminToken(token) {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem("xpert_admin_token", token);
  else localStorage.removeItem("xpert_admin_token");
}

// ─── The one true clear ──────────────────────────────────────────────────────

/**
 * Remove every user-scoped key from localStorage and notify subscribers.
 *
 * Called on logout(), at the start of login()/register() (defence-in-depth
 * in case a prior session left half-state behind), and on 401 token-refresh
 * failure.
 */
export function clearAllUserState() {
  if (typeof window === "undefined") return;
  for (const key of USER_SCOPED_KEYS) {
    localStorage.removeItem(key);
  }
  _emitCleared();
}
